// Inspired by React Spectrum's approach to handling pending button state.
// @see https://github.com/adobe/react-spectrum/blob/%40react-spectrum/button%403.17.6/packages/react-aria-components/src/Button.tsx#L175

const FOCUS_EVENT_PATTERN = /Focus|Blur/;

type DisableReason = {
  isLoading?: boolean;
  isDisabled?: boolean;
};

/**
 * Strips event handlers and navigation props when the element is inactive.
 *
 * - `isLoading` — preserves focus/blur so keyboard navigation and screen
 *   readers still work while the element is busy.
 * - `isDisabled` — strips **all** event handlers including focus/blur.
 *
 * @example
 * ```tsx
 * disableInteractions(props, { isLoading: true });   // keeps onFocus/onBlur
 * disableInteractions(props, { isDisabled: true });   // strips everything
 * ```
 */
export function disableInteractions<T extends object>(
  props: T,
  reason: DisableReason
): T {
  const isActive = !(reason.isLoading || reason.isDisabled);

  if (isActive) {
    return props;
  }

  const preserveFocus = Boolean(reason.isLoading) && !reason.isDisabled;

  // Generic type T is read-only, cast to mutable record for property modification
  const result = { ...props } as Record<string, unknown>;

  for (const key of Object.keys(result)) {
    if (key.startsWith('on')) {
      if (preserveFocus && FOCUS_EVENT_PATTERN.test(key)) {
        continue;
      }
      result[key] = undefined;
    }
  }

  // Disable navigation
  result['href'] = undefined;
  result['to'] = undefined;
  result['target'] = undefined;

  return result as T;
}

export type { DisableReason };
